import React, { useEffect } from 'react';
import { useRouter } from 'next/router';

import { usePinContext } from './PinContext';
import PinEntry from '@/domains/shared/components/pin/PinEntry';
import PinSetup from '@/domains/shared/components/pin/PinSetup';
import { useUnlockMachine } from '@/domains/shared/identity/useUnlockMachine';
import { usePlatformInfo } from '../../hooks/useIsNative';

/**
 * PinManager is now a thin presenter over the unlock state machine.
 *
 * All the orchestration that used to live here — boot gating, biometric
 * attempt timing, PIN attempt counting, the 60s re-lock — moved to
 * `useUnlockMachine`. This component's only responsibility is mapping
 * each machine state to the appropriate UI.
 *
 * The forgot-PIN redirect remains here because it's a routing concern
 * that consumes `PinContext`, not the unlock state machine.
 */

interface PinManagerProps {
  children: React.ReactNode;
}

const PIN_RESET_PATH = '/hive/accounts/pin-reset';

const HoldingScreen = () => <div className="h-screen w-screen bg-background" />;

const PinManager: React.FC<PinManagerProps> = ({ children }) => {
  const router = useRouter();
  const { isForgotPin, handlePinReset } = usePinContext();
  const { state, actions } = useUnlockMachine();

  const { isNative } = usePlatformInfo();

  useEffect(() => {
    if (isForgotPin && router.pathname !== PIN_RESET_PATH) {
      router.replace(PIN_RESET_PATH);
    }
  }, [isForgotPin, router]);

  if (isNative === undefined) {
    return <HoldingScreen />;
  }

  if (!isNative) {
    return <>{children}</>;
  }

  if (isForgotPin) {
    // PinManager wraps every page, including the pin-reset page itself.
    // Returning null here would blank the screen *after* the redirect
    // lands, leaving the user with no way to confirm the reset. Render
    // the routed page once we're on it; show a holding screen only
    // during the brief router.replace transition above.
    if (router.pathname === PIN_RESET_PATH) {
      return <>{children}</>;
    }
    return <HoldingScreen />;
  }

  switch (state.kind) {
    case 'booting':
    case 'attempting-biometric':
      return <HoldingScreen />;

    case 'signed-out':
      return <>{children}</>;

    case 'awaiting-pin-setup':
      return <PinSetup onSuccess={actions.pinSetupCompleted} />;

    case 'awaiting-pin-entry':
      return (
        <PinEntry
          onSuccess={actions.pinCorrect}
          onIncorrectPin={actions.pinWrong}
          onForgotPin={handlePinReset}
          incorrectAttempts={state.attempts}
        />
      );

    case 'logging-out':
      // 3 wrong PINs has fired logout; freeze the PIN screen on attempts=3
      // until Firebase publishes the auth-state change and the machine
      // transitions to signed-out. Matches the pre-refactor UX.
      return (
        <PinEntry
          onSuccess={actions.pinCorrect}
          onIncorrectPin={actions.pinWrong}
          onForgotPin={handlePinReset}
          incorrectAttempts={3}
        />
      );

    case 'unlocked':
      return <>{children}</>;
  }
};

export default PinManager;
